<script>
/* eslint-env browser */
/* global FB */
import { useVuelidate } from '@vuelidate/core';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import { required } from '@vuelidate/validators';
import LoadingState from 'dashboard/components/widgets/LoadingState.vue';

import ChannelApi from '../../../../../api/channels';
import PageHeader from '../../SettingsSubPageHeader.vue';
import router from '../../../../index';
import { useBranding } from 'shared/composables/useBranding';
import NextButton from 'dashboard/components-next/button/Button.vue';

import { loadScript } from 'dashboard/helper/DOMHelpers';
import * as Sentry from '@sentry/vue';

export default {
  components: {
    LoadingState,
    PageHeader,
    NextButton,
  },
  setup() {
    const { accountId } = useAccount();
    const { replaceInstallationName } = useBranding();
    return {
      accountId,
      replaceInstallationName,
      v$: useVuelidate(),
    };
  },
  data() {
    return {
      currentStep: 'login', // Skip credentials form, go directly to login
      isCreating: false,
      hasError: false,
      omniauth_token: '',
      user_access_token: '',
      channel: 'facebook',
      selectedPage: { name: null, id: null },
      pageName: '',
      pageList: [],
      emptyStateMessage: this.$t('INBOX_MGMT.DETAILS.LOADING_FB'),
      errorStateMessage: '',
      errorStateDescription: '',
      hasLoginStarted: false,
      createSeparateInstagram: false,
      instagramAccountName: '',
      credentials: {
        appId: '',
        appSecret: '',
        verifyToken: '',
        apiVersion: 'v18.0',
      },
    };
  },

  validations: {
    // credentials validation removed since credentials form is commented out
    pageName: {
      required,
    },
    selectedPage: {
      isEmpty() {
        return this.selectedPage !== null && !!this.selectedPage.name;
      },
    },
  },

  computed: {
    showLoader() {
      return !this.user_access_token || this.isCreating;
    },
    getSelectablePages() {
      // Show pages that don't exist OR have Instagram (even if page exists)
      return this.pageList.filter(
        item => !item.exists || item.instagram_account
      );
    },
  },

  mounted() {
    window.fbAsyncInit = this.runFBInit;
  },

  methods: {
    async saveCredentials() {
      this.v$.credentials.$touch();
      if (this.v$.credentials.$invalid) return;

      try {
        this.isCreating = true;
        await this.updateFacebookConfig(this.credentials);
        this.currentStep = 'login';
      } catch (error) {
        console.error('Error saving Facebook credentials:', error);
        this.hasError = true;
        this.errorStateMessage = 'Failed to save Facebook credentials';
        this.errorStateDescription = error.message || 'Please try again';
      } finally {
        this.isCreating = false;
      }
    },

    async updateFacebookConfig(credentials) {
      const configData = {
        FB_APP_ID: credentials.appId,
        FB_APP_SECRET: credentials.appSecret,
        FB_VERIFY_TOKEN: credentials.verifyToken,
        FACEBOOK_API_VERSION: credentials.apiVersion,
      };

      // Update window.chatwootConfig immediately for UI consistency
      if (window.chatwootConfig) {
        window.chatwootConfig.fbAppId = credentials.appId;
        window.chatwootConfig.fbApiVersion = credentials.apiVersion;
      }

      console.log('Facebook config to save:', configData);
    },

    goBackToCredentials() {
      this.currentStep = 'credentials';
      this.hasError = false;
      this.hasLoginStarted = false;
    },

    async startLogin() {
      this.hasLoginStarted = true;
      try {
        // this will load the SDK in a promise, and resolve it when the sdk is loaded
        // in case the SDK is already present, it will resolve immediately
        await this.loadFBsdk();
        this.runFBInit(); // run init anyway, `tryFBlogin` won't wait for `fbAsyncInit` otherwise.
        this.tryFBlogin(); // make an attempt to login
      } catch (error) {
        if (error.name === 'ScriptLoaderError') {
          // if the error was related to script loading, we show a toast
          useAlert(this.$t('INBOX_MGMT.DETAILS.ERROR_FB_LOADING'));
        } else {
          // if the error was anything else, we capture it and show a toast
          Sentry.captureException(error);
          useAlert(this.$t('INBOX_MGMT.DETAILS.ERROR_FB_AUTH'));
        }
      }
    },

    setPageName({ name }) {
      this.v$.selectedPage.$touch();
      this.pageName = name;
    },

    initChannelAuth(channel) {
      if (channel === 'facebook') {
        this.loadFBsdk();
      }
    },

    runFBInit() {
      FB.init({
        appId: window.chatwootConfig.fbAppId,
        xfbml: true,
        version: window.chatwootConfig.fbApiVersion,
        status: true,
      });
      window.fbSDKLoaded = true;
      FB.AppEvents.logPageView();
    },

    async loadFBsdk() {
      return loadScript('https://connect.facebook.net/en_US/sdk.js', {
        id: 'facebook-jssdk',
      });
    },

    tryFBlogin() {
      FB.login(
        response => {
          this.hasError = false;
          if (response.status === 'connected') {
            this.fetchPages(response.authResponse.accessToken);
          } else if (response.status === 'not_authorized') {
            // eslint-disable-next-line no-console
            console.error('FACEBOOK AUTH ERROR', response);
            this.hasError = true;
            // The person is logged into Facebook, but not your app.
            this.errorStateMessage = this.$t(
              'INBOX_MGMT.DETAILS.ERROR_FB_UNAUTHORIZED'
            );
            this.errorStateDescription = this.$t(
              'INBOX_MGMT.DETAILS.ERROR_FB_UNAUTHORIZED_HELP'
            );
          } else {
            // eslint-disable-next-line no-console
            console.error('FACEBOOK AUTH ERROR', response);
            this.hasError = true;
            // The person is not logged into Facebook, so we're not sure if
            // they are logged into this app or not.
            this.errorStateMessage = this.$t(
              'INBOX_MGMT.DETAILS.ERROR_FB_AUTH'
            );
            this.errorStateDescription = '';
          }
        },
        {
          scope:
            'pages_manage_metadata,business_management,pages_messaging,instagram_basic,pages_show_list,pages_read_engagement,pages_manage_posts,pages_manage_engagement,pages_read_user_content,read_insights,instagram_manage_messages,instagram_content_publish,instagram_manage_comments,instagram_manage_insights,catalog_management',
          // Without this, Facebook shows the consent dialog only on a user's
          // FIRST authorization. Every later attempt silently reuses whatever
          // was granted then - so someone who connected before without picking
          // a page never sees the page selector again, and /me/accounts keeps
          // returning [] with no error to explain it. 'reauthorize' forces the
          // full flow, including page selection, every time.
          auth_type: 'reauthorize',
        }
      );
    },

    async fetchPages(_token) {
      try {
        const response = await ChannelApi.fetchFacebookPages(
          _token,
          this.accountId
        );
        const {
          data: { data },
        } = response;
        this.pageList = data.page_details;
        this.user_access_token = data.user_access_token;

        // Facebook answers /me/accounts with an empty array - not an error -
        // when the user authorized the app without granting any page. Without
        // this the agent just sees an empty dropdown and nothing to explain it.
        if (!this.pageList.length) {
          this.hasError = true;
          this.errorStateMessage = this.$t(
            'INBOX_MGMT.DETAILS.ERROR_FB_NO_PAGES'
          );
          this.errorStateDescription = this.$t(
            'INBOX_MGMT.DETAILS.ERROR_FB_NO_PAGES_HELP'
          );
        }
      } catch (error) {
        // Previously swallowed, which made a genuine API failure look exactly
        // like "no pages" and left nothing to diagnose from.
        this.hasError = true;
        this.errorStateMessage = this.$t(
          'INBOX_MGMT.DETAILS.ERROR_FB_PAGES_FETCH'
        );
        this.errorStateDescription = '';
      }
    },

    channelParams() {
      return {
        user_access_token: this.user_access_token,
        page_access_token: this.selectedPage.access_token,
        page_id: this.selectedPage.id,
        inbox_name: this.selectedPage.name,
      };
    },

    async createChannel() {
      this.v$.$touch();
      if (!this.v$.$error) {
        this.emptyStateMessage = this.$t('INBOX_MGMT.DETAILS.CREATING_CHANNEL');
        this.isCreating = true;
        try {
          // Create Facebook Page channel
          const fbData = await this.$store.dispatch(
            'inboxes/createFBChannel',
            this.channelParams()
          );

          // If user wants separate Instagram channel
          if (
            this.createSeparateInstagram &&
            this.selectedPage.instagram_account
          ) {
            await this.createInstagramChannel();
          }

          router.replace({
            name: 'settings_inboxes_add_agents',
            params: { page: 'new', inbox_id: fbData.id },
          });
        } catch (error) {
          this.isCreating = false;
        }
      }
    },
    async createInstagramChannel() {
      const instagramParams = {
        user_access_token: this.user_access_token,
        page_access_token: this.selectedPage.access_token,
        instagram_id: this.selectedPage.instagram_account.id,
        inbox_name:
          this.instagramAccountName ||
          `@${this.selectedPage.instagram_account.username}`,
      };

      await ChannelApi.createInstagramFromFacebook(
        instagramParams,
        this.accountId
      );
    },
  },
};
</script>

<template>
  <div class="w-full h-full col-span-6 p-6 overflow-auto">
    <!-- Step 1: Credentials Form - COMMENTED OUT FOR ADMIN-LEVEL CONFIG -->
    <!--
    <div
      v-if="currentStep === 'credentials'"
      class="flex flex-col items-center justify-start h-full"
    >
      <div class="w-full max-w-md">
        <div class="text-center mb-8">
          <h6 class="text-2xl font-medium mb-2">Configure Facebook App</h6>
          <p class="text-sm text-n-slate-11">
            Enter your Facebook app credentials to enable Facebook channel
          </p>
        </div>

        <form class="space-y-6" @submit.prevent="saveCredentials">
          <div>
            <label class="block text-sm font-medium text-n-slate-12 mb-2">
              Facebook App ID *
            </label>
            <input
              v-model="credentials.appId"
              type="text"
              class="w-full px-3 py-2 border border-n-weak rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter your Facebook App ID"
              :class="{ 'border-red-500': v$.credentials.appId.$error }"
            />
            <p
              v-if="v$.credentials.appId.$error"
              class="mt-1 text-sm text-red-600"
            >
              Facebook App ID is required
            </p>
          </div>

          <div>
            <label class="block text-sm font-medium text-n-slate-12 mb-2">
              Facebook App Secret *
            </label>
            <input
              v-model="credentials.appSecret"
              type="password"
              class="w-full px-3 py-2 border border-n-weak rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter your Facebook App Secret"
              :class="{ 'border-red-500': v$.credentials.appSecret.$error }"
            />
            <p
              v-if="v$.credentials.appSecret.$error"
              class="mt-1 text-sm text-red-600"
            >
              Facebook App Secret is required
            </p>
          </div>

          <div>
            <label class="block text-sm font-medium text-n-slate-12 mb-2">
              Webhook Verify Token *
            </label>
            <input
              v-model="credentials.verifyToken"
              type="text"
              class="w-full px-3 py-2 border border-n-weak rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter a secure verify token"
              :class="{ 'border-red-500': v$.credentials.verifyToken.$error }"
            />
            <p
              v-if="v$.credentials.verifyToken.$error"
              class="mt-1 text-sm text-red-600"
            >
              Webhook verify token is required
            </p>
          </div>

          <div>
            <label class="block text-sm font-medium text-n-slate-12 mb-2">
              API Version
            </label>
            <select
              v-model="credentials.apiVersion"
              class="w-full px-3 py-2 border border-n-weak rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="v18.0">v18.0 (Recommended)</option>
              <option value="v17.0">v17.0</option>
              <option value="v16.0">v16.0</option>
            </select>
          </div>

          <button
            type="submit"
            class="w-full flex items-center justify-center px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
            :disabled="isCreating"
          >
            <span v-if="isCreating" class="mr-2">
              <svg
                class="w-4 h-4 animate-spin"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  class="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  stroke-width="4"
                />
                <path
                  class="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
            </span>
            {{ isCreating ? 'Saving...' : 'Save & Continue' }}
          </button>
        </form>

        <div class="mt-6 p-4 bg-blue-50 rounded-lg">
          <h6 class="text-sm font-medium text-blue-900 mb-2">
            Need help getting these credentials?
          </h6>
          <p class="text-xs text-blue-800">
            1. Go to
            <a
              href="https://developers.facebook.com/"
              target="_blank"
              class="underline"
              >Meta Developer Console</a><br />
            2. Create a new app or select existing app<br />
            3. Add Messenger product to your app<br />
            4. Copy your App ID and App Secret<br />
            5. Set up webhooks with your verify token
          </p>
        </div>
      </div>
    </div>
    -->

    <!-- Step 2: Facebook Login -->
    <div
      v-if="currentStep === 'login' && !hasLoginStarted"
      class="flex flex-col items-center justify-center h-full text-center"
    >
      <a href="#" @click="startLogin()">
        <img
          class="w-auto h-10 rounded-md"
          src="~dashboard/assets/images/channels/facebook_login.png"
          alt="Facebook-logo"
        />
      </a>
      <p class="py-6">
        {{ replaceInstallationName($t('INBOX_MGMT.ADD.FB.HELP')) }}
      </p>
      <!-- Back to Credentials button removed since credentials form is commented out -->
    </div>
    <div v-else>
      <div v-if="hasError" class="max-w-lg mx-auto text-center">
        <h5>{{ errorStateMessage }}</h5>
        <p
          v-if="errorStateDescription"
          v-dompurify-html="errorStateDescription"
        />
        <!-- Back to Credentials button removed since credentials form is commented out -->
      </div>
      <LoadingState v-else-if="showLoader" :message="emptyStateMessage" />
      <form
        v-else
        class="flex flex-col flex-wrap mx-0"
        @submit.prevent="createChannel()"
      >
        <div class="w-full">
          <PageHeader
            :header-title="$t('INBOX_MGMT.ADD.DETAILS.TITLE')"
            :header-content="
              replaceInstallationName($t('INBOX_MGMT.ADD.DETAILS.DESC'))
            "
          />
        </div>
        <div class="w-3/5">
          <div class="w-full">
            <div class="input-wrap" :class="{ error: v$.selectedPage.$error }">
              {{ $t('INBOX_MGMT.ADD.FB.CHOOSE_PAGE') }}
              <multiselect
                v-model="selectedPage"
                close-on-select
                allow-empty
                :options="getSelectablePages"
                track-by="id"
                label="name"
                :select-label="$t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
                :deselect-label="$t('FORMS.MULTISELECT.ENTER_TO_REMOVE')"
                :placeholder="$t('INBOX_MGMT.ADD.FB.PICK_A_VALUE')"
                selected-label
                @select="setPageName"
              />
              <span v-if="v$.selectedPage.$error" class="message">
                {{ $t('INBOX_MGMT.ADD.FB.CHOOSE_PLACEHOLDER') }}
              </span>
            </div>
          </div>
          <div class="w-full">
            <label :class="{ error: v$.pageName.$error }">
              {{ $t('INBOX_MGMT.ADD.FB.INBOX_NAME') }}
              <input
                v-model="pageName"
                type="text"
                :placeholder="$t('INBOX_MGMT.ADD.FB.PICK_NAME')"
                @input="v$.pageName.$touch"
              />
              <span v-if="v$.pageName.$error" class="message">
                {{ $t('INBOX_MGMT.ADD.FB.ADD_NAME') }}
              </span>
            </label>
          </div>

          <!-- Instagram Account Detection -->
          <div
            v-if="selectedPage?.instagram_account"
            class="w-full p-4 bg-blue-50 border border-blue-200 rounded-lg mt-4"
          >
            <div class="flex items-center gap-2 mb-2">
              <input
                id="create-instagram"
                v-model="createSeparateInstagram"
                type="checkbox"
                class="h-4 w-4"
              />
              <label for="create-instagram" class="font-medium cursor-pointer">
                Also create separate Instagram channel
              </label>
            </div>
            <p class="text-sm text-gray-600 mb-3">
              Instagram account detected: @{{
                selectedPage.instagram_account.username
              }}
            </p>

            <div v-if="createSeparateInstagram" class="mt-3">
              <label class="block text-sm font-medium mb-1">
                Instagram Inbox Name
              </label>
              <input
                v-model="instagramAccountName"
                type="text"
                :placeholder="`@${selectedPage.instagram_account.username}`"
                class="w-full px-3 py-2 border border-gray-300 rounded"
              />
              <p class="text-xs text-gray-500 mt-1">
                Leave empty to use @{{
                  selectedPage.instagram_account.username
                }}
              </p>
            </div>
          </div>

          <div class="w-full text-right">
            <NextButton
              type="submit"
              :label="$t('INBOX_MGMT.ADD.FB.CREATE_INBOX')"
            />
          </div>
        </div>
      </form>
    </div>
  </div>
</template>
